'use client';

import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  company?: string;
  message?: string;
  turnstile?: string;
}

export function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const turnstileRef = useRef<TurnstileInstance>(null);
  const honeypotRef = useRef<HTMLInputElement>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    if (!turnstileToken) {
      newErrors.turnstile = 'Please complete the verification';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('/api/contact', {
        credentials: 'include',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          turnstileToken,
          _gotcha: honeypotRef.current?.value ?? '',
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.details) {
          const fieldErrors: FormErrors = {};
          for (const [key, messages] of Object.entries(result.details)) {
            fieldErrors[key as keyof FormErrors] = (messages as string[])[0];
          }
          setErrors(fieldErrors);
        }
        setSubmitStatus('error');
        turnstileRef.current?.reset();
        setTurnstileToken('');
        return;
      }

      setSubmitStatus('success');
      setFormData({ name: '', email: '', company: '', message: '' });
      setErrors({});
      turnstileRef.current?.reset();
      setTurnstileToken('');

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } catch (_error) {
      console.error('Contact form submission error:', _error);
      setSubmitStatus('error');
      turnstileRef.current?.reset();
      setTurnstileToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="John Doe"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'border-red-500' : ''}
              disabled={isSubmitting}
              suppressHydrationWarning
            />
            {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="john@company.com"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'border-red-500' : ''}
              disabled={isSubmitting}
              suppressHydrationWarning
            />
            {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="company">Company *</Label>
          <Input
            id="company"
            name="company"
            type="text"
            placeholder="Acme Corp"
            value={formData.company}
            onChange={handleChange}
            className={errors.company ? 'border-red-500' : ''}
            disabled={isSubmitting}
            suppressHydrationWarning
          />
          {errors.company && <p className="text-sm text-red-500">{errors.company}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">Message *</Label>
          <Textarea
            id="message"
            name="message"
            placeholder="Tell us about your AI operations and what you're looking to achieve..."
            rows={5}
            value={formData.message}
            onChange={handleChange}
            className={errors.message ? 'border-red-500' : ''}
            disabled={isSubmitting}
            suppressHydrationWarning
          />
          {errors.message && <p className="text-sm text-red-500">{errors.message}</p>}
        </div>

        {/* Honeypot — invisible to humans, bots fill it */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px' }}>
          <input
            ref={honeypotRef}
            type="text"
            name="_gotcha"
            tabIndex={-1}
            autoComplete="off"
            suppressHydrationWarning
          />
        </div>

        {/* Cloudflare Turnstile — invisible mode */}
        {process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY && (
          <Turnstile
            ref={turnstileRef}
            siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY}
            onSuccess={setTurnstileToken}
            onError={() => {
              setTurnstileToken('');
              setErrors((prev) => ({
                ...prev,
                turnstile: 'Verification failed. Please try again.',
              }));
            }}
            onExpire={() => {
              setTurnstileToken('');
            }}
            options={{ size: 'invisible' }}
          />
        )}
        {errors.turnstile && <p className="text-sm text-red-500">{errors.turnstile}</p>}

        <Button
          type="submit"
          className="w-full gradient-primary text-white hover:opacity-90 transition-opacity"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </Button>

        {submitStatus === 'success' && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
            Thank you for your message! We&apos;ll get back to you within 24 hours.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            Something went wrong. Please try again or email us directly at contact@opsvis.ai
          </div>
        )}
      </form>
    </div>
  );
}
